import { useRef, useState } from "react";
import { FRACTION_SYMBOLS, normalizeQuantityFractions, useT } from "@reciperoll/core";
import {
  newIngredientRow,
  ingredientRowsToText,
  textToIngredientRows,
  type IngredientRow,
} from "../utils/recipeFormRows";
import { SortableList, GripHandle } from "./SortableList";

export type { IngredientRow };

const iconBtn =
  "w-7 h-7 shrink-0 flex items-center justify-center rounded-md text-ink-faint hover:text-ink hover:bg-surface-2 disabled:opacity-30 disabled:hover:bg-transparent";
const rowInput =
  "rounded-lg border border-line bg-surface dark:bg-surface-2 px-2.5 py-1.5 text-sm text-ink outline-none focus:border-terracotta focus:ring-1 focus:ring-terracotta";

export function RecipeIngredientEditor({
  rows,
  onChange,
}: {
  rows: IngredientRow[];
  onChange: (rows: IngredientRow[]) => void;
}) {
  const t = useT();
  const [bulk, setBulk] = useState(false);
  const [bulkText, setBulkText] = useState("");
  // Rows whose (possibly still empty) substitute sub-row is open. Rows with
  // saved sub fields show it regardless, so this only tracks fresh opens.
  const [subOpen, setSubOpen] = useState<Set<string>>(new Set());
  // The quantity field currently focused — drives which row shows the fraction
  // quick-pick strip, and which input a tapped fraction gets inserted into.
  const [fractionRowId, setFractionRowId] = useState<string | null>(null);
  const focusedInput = useRef<HTMLInputElement | null>(null);

  const enterBulk = () => {
    setBulkText(ingredientRowsToText(rows));
    setBulk(true);
  };
  const applyBulk = () => {
    onChange(textToIngredientRows(bulkText));
    setBulk(false);
  };

  const patch = (id: string, p: Partial<IngredientRow>) =>
    onChange(rows.map((r) => (r.id === id ? { ...r, ...p } : r)));
  const remove = (id: string) => onChange(rows.filter((r) => r.id !== id));

  const showSub = (row: IngredientRow) =>
    !row.isGroup && (!!row.subName || !!row.subQty || subOpen.has(row.id));
  const openSub = (id: string) => setSubOpen((prev) => new Set(prev).add(id));
  const removeSub = (id: string) => {
    patch(id, { subQty: "", subName: "" });
    setSubOpen((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  // Insert a fraction symbol at the cursor of the focused quantity field.
  const insertFraction = (sym: string) => {
    const el = focusedInput.current;
    if (!el || !fractionRowId) return;
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    patch(fractionRowId, { qty: el.value.slice(0, start) + sym + el.value.slice(end) });
    const pos = start + sym.length;
    // Restore focus + cursor after the controlled input re-renders.
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  };

  return (
    <div>
      <div className="flex justify-end mb-1">
        <button
          type="button"
          onClick={bulk ? applyBulk : enterBulk}
          className="text-xs font-semibold text-ink-soft hover:text-ink"
        >
          {bulk ? t("common.done") : t("form.bulkEdit")}
        </button>
      </div>

      {bulk ? (
        <textarea
          className="w-full rounded-lg border border-line bg-surface dark:bg-surface-2 px-3 py-2 font-mono text-sm text-ink outline-none focus:border-terracotta focus:ring-1 focus:ring-terracotta"
          rows={10}
          value={bulkText}
          onChange={(e) => setBulkText(e.target.value)}
          placeholder={t("form.ingredientBulkPlaceholder")}
          autoFocus
        />
      ) : (
        <div>
          <SortableList items={rows} onReorder={onChange} className="space-y-2" renderItem={(row, handle) => (
            <div>
              <div className="flex items-center gap-1.5">
                <GripHandle handle={handle} className="w-6 h-8" />

                {row.isGroup ? (
                  <input
                    className={`${rowInput} flex-1 font-bold uppercase tracking-wide text-ink-soft`}
                    value={row.name}
                    onChange={(e) => patch(row.id, { name: e.target.value })}
                    placeholder={t("form.sectionHeadingPlaceholder")}
                  />
                ) : (
                  <>
                    <input
                      className={`${rowInput} w-24`}
                      value={row.qty}
                      onChange={(e) => patch(row.id, { qty: e.target.value })}
                      onFocus={(e) => {
                        setFractionRowId(row.id);
                        focusedInput.current = e.currentTarget;
                      }}
                      onBlur={(e) => {
                        // Normalise fractions when leaving the field: supported → symbol
                        // (1/2 → ½), unsupported → decimal (1/6 → 0.167).
                        patch(row.id, { qty: normalizeQuantityFractions(e.currentTarget.value) });
                        setFractionRowId((id) => (id === row.id ? null : id));
                      }}
                      placeholder={t("form.qtyPlaceholder")}
                    />
                    <input
                      className={`${rowInput} flex-1`}
                      value={row.name}
                      onChange={(e) => patch(row.id, { name: e.target.value })}
                      placeholder={t("form.ingredient")}
                    />
                  </>
                )}

                {!row.isGroup && !showSub(row) && (
                  <button
                    type="button"
                    className={iconBtn}
                    onClick={() => openSub(row.id)}
                    aria-label={t("form.addSubstitute")}
                    title={t("form.addSubstitute")}
                  >
                    ⇄
                  </button>
                )}
                <button type="button" className={iconBtn} onClick={() => remove(row.id)} aria-label={t("common.remove")}>✕</button>
              </div>

              {showSub(row) && (
                <div className="flex items-center gap-1.5 mt-1.5 ml-7">
                  <span
                    className="w-6 shrink-0 text-center text-xs font-semibold text-ink-faint"
                    title={t("form.substitute")}
                  >
                    ⇄
                  </span>
                  <input
                    className={`${rowInput} w-24`}
                    value={row.subQty}
                    onChange={(e) => patch(row.id, { subQty: e.target.value })}
                    onBlur={(e) =>
                      patch(row.id, { subQty: normalizeQuantityFractions(e.currentTarget.value) })
                    }
                    placeholder={t("form.qtyPlaceholder")}
                  />
                  <input
                    className={`${rowInput} flex-1`}
                    value={row.subName}
                    onChange={(e) => patch(row.id, { subName: e.target.value })}
                    placeholder={t("form.substituteNamePlaceholder")}
                  />
                  <button
                    type="button"
                    className={iconBtn}
                    onClick={() => removeSub(row.id)}
                    aria-label={t("form.removeSubstitute")}
                  >
                    ✕
                  </button>
                </div>
              )}

              {fractionRowId === row.id && !row.isGroup && (
                <div className="flex gap-1 mt-1.5 ml-7">
                  {FRACTION_SYMBOLS.map((sym) => (
                    <button
                      key={sym}
                      type="button"
                      // Keep the quantity field focused so the insert lands there.
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => insertFraction(sym)}
                      aria-label={t("form.insertFraction", { symbol: sym })}
                      className="w-7 h-7 rounded-md bg-surface-2 hover:bg-line text-sm font-semibold text-ink cursor-pointer"
                    >
                      {sym}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )} />

          <div className="flex gap-2 pt-3">
            <button
              type="button"
              onClick={() => onChange([...rows, newIngredientRow(false)])}
              className="text-sm font-semibold text-terracotta hover:text-terracotta-dark"
            >
              + {t("form.ingredient")}
            </button>
            <button
              type="button"
              onClick={() => onChange([...rows, newIngredientRow(true)])}
              className="text-sm font-semibold text-ink-soft hover:text-ink"
            >
              + {t("form.heading")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
